import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { ApiSchedule } from '../../types';
import { ButtonLink } from '../Form/Button';
import { renderTemplate } from '../utils';
import './UpcomingOrder.css';
import { formatCalendarDate, lastOrderOf, upcomingOrdersOf } from './utils';

/**
 * The orders a refill has already created, which are the only things on the card that already cost
 * money. They are told apart from the schedule in as many words: these exist, have a number and a
 * date, and can be changed until they leave the warehouse - the schedule is what comes after them.
 *
 * The change is offered only where the api has said the order may still be changed - the same
 * `isOrderEditable` the change page is gated on, so the card cannot send a customer to a page that
 * bounces them straight back to the order list. Without that word, there is only the way in.
 */
export const UpcomingOrder = ({ schedule, paused }: { schedule: ApiSchedule; paused: boolean }): JSX.Element => {
	const translations = useTranslation();
	const t = { ...translations.subscriptions, urls: translations.routes };
	const { dateTimeFormat } = useFormatting();

	const upcoming = upcomingOrdersOf(schedule);
	const urlFor = (order: number) => `${t.urls.yourOrders}/${order}`;

	/*
	 * Everything here is true only until the soonest order leaves the warehouse: after that it is
	 * no longer on its way and no longer changeable. A page left open would go on offering the
	 * change, and the change page would turn it down - the dead end this is meant to prevent. One
	 * render at that moment settles it. setTimeout cannot hold more than about 24 days and a longer
	 * delay fires at once, so a window that far off is left to the next render instead.
	 *
	 * It counts rather than flips: a daily refill has two orders on their way, and a second flag
	 * set to the value it already held is no change at all, so react would skip the render and
	 * leave the second order standing there with its change link live.
	 */
	const soonest = upcoming.length ? upcoming[0].dispatch.getTime() : 0;
	const [, aDispatchHasPassed] = useState(0);

	useEffect(() => {
		const msLeft = soonest - Date.now();

		if (!soonest || msLeft <= 0 || msLeft > 0x7fffffff) return;

		const timer = setTimeout(() => aDispatchHasPassed((passed) => passed + 1), msLeft);

		return () => clearTimeout(timer);
	}, [soonest]);

	if (!upcoming.length) {
		const last = lastOrderOf(schedule);

		if (!last) return <></>;

		return (
			<p className="subscription-last-order">
				{t.lastOrder}:{' '}
				<Link className="text-link" to={urlFor(last.order)}>
					#{last.order} · {formatCalendarDate(last.dispatch)} <span aria-hidden="true">&rarr;</span>
				</Link>
			</p>
		);
	}

	return (
		<div className="subscription-upcoming-order">
			{/* a daily refill can have two orders in flight at once, each with a deadline of its own */}
			<h3>{upcoming.length > 1 ? t.upcomingOrdersTitle : t.upcomingOrderTitle}</h3>
			{/* pausing stops what comes next, not what has already been created */}
			{paused && (
				<p>
					<strong>{t.upcomingOrderWhilePaused}</strong>
				</p>
			)}
			{upcoming.map(({ order, dispatch, editable }) => (
				<div className="subscription-upcoming-order-item" key={order}>
					<p>
						{renderTemplate(t.upcomingOrderPlanned, { number: order, date: formatCalendarDate(dispatch) })}
					</p>
					{/* only an order that may actually be changed is told it can be */}
					{editable && (
						<p>{renderTemplate(t.upcomingOrderChangeUntil, { deadline: dateTimeFormat(dispatch) })}</p>
					)}
					<div className="subscription-upcoming-order-actions">
						{editable && (
							<ButtonLink className="small" linkTo={`${urlFor(order)}${t.urls.edit}`}>
								{t.changeOrder}
							</ButtonLink>
						)}
						<ButtonLink className={`small${editable ? ' secondary' : ''}`} linkTo={urlFor(order)}>
							{t.schedule.viewOrder}
						</ButtonLink>
					</div>
				</div>
			))}
		</div>
	);
};
